import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';
import { roleService } from '../../services/roleService';
import { User, UpdateUserRequest } from '../../types/user';
import { Role } from '../../types/role';
import { useToast } from '../../contexts/ToastContext';

interface EditUserModalProps {
  isOpen: boolean;
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, user, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setSelectedRoleId(user.roleId);
    }
  }, [user]);

  useEffect(() => {
    if (isOpen && roles.length === 0) {
      roleService
        .getSystemRoles()
        .then((res) => {
          if (res.success) setRoles(res.data);
        })
        .catch(() => {});
    }
  }, [isOpen, roles.length]);

  const handleSave = async () => {
    if (!user || !firstName.trim() || !lastName.trim()) return;
    setIsSubmitting(true);
    try {
      const request: UpdateUserRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      };
      await userService.update(user.id, request);
      toast.success('User updated successfully');
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
      <div
        className="bg-white w-full max-w-lg rounded-2xl border border-slate-200 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-4 flex items-center justify-between">
          <h2 className="text-xl font-black tracking-tight text-slate-900">Edit User</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-slate-400 hover:text-slate-900 transition-colors disabled:opacity-50"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-6 space-y-5">
          {/* Email (read-only) */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500">Email</label>
            <input
              type="text"
              value={user.email}
              disabled
              className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm text-slate-400 cursor-not-allowed"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500">First Name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all text-slate-700"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500">Last Name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm focus:ring-1 focus:ring-primary focus:border-primary transition-all text-slate-700"
              />
            </div>
          </div>

          {/* Role (display only) */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500">Role</label>
            <select
              value={selectedRoleId}
              disabled
              className="w-full bg-slate-50 border-slate-200 rounded-lg py-3 px-4 text-sm appearance-none text-slate-400 cursor-not-allowed"
            >
              <option value="">--</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
              {/* Fallback if roles haven't loaded yet */}
              {roles.length === 0 && user.role && (
                <option value={user.roleId}>{user.role.name}</option>
              )}
            </select>
            <p className="text-[10px] text-slate-400">Role changes require the Roles page.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-6 py-2.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting || !firstName.trim() || !lastName.trim()}
            className="bg-primary hover:bg-primary/90 text-white px-8 py-2.5 rounded-lg font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;
